import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fade, IconButton, Modal, Slide } from '@material-ui/core';
import classes from './styles.module.css';
import { getCityByUrl, isFranquisedCity } from '@app/utils/city';
import { CliqueiAcheiData, Story, StoryPreview } from '@app/types';
import { APICliqueiAchei } from '@app/services/cliqueiachei-api';
import { extractPromotionPrizes, getRandomArrayItem, EXAMPLE1, EXAMPLE2, createPromotionMessage } from './helpers';
import { APIJsonConfigs, MenuWebMessage, MenuWebMessageParser } from '@app/services/cliqueiachei-json-configs';
import { Close } from '@material-ui/icons';
import parseHTMLString from 'html-react-parser';
import { Systems, useGetSystemInformation } from '@app/utils/device';
import * as ga from '@app/utils/ga';
import { useClientArea } from '@app/contexts/ClientArea';
import { SessionStorage } from '@app/utils/storage';

const MESSAGE: MenuWebMessage = {
  title: 'Teste',
  description: 'Uma descrição',
};

const LINKS = {
  ANDROID: 'https://play.google.com/store/apps/details?id=br.com.cliqueiachei',
  IOS: 'https://apps.apple.com/br/app/cliquei-achei/id1081949439',
};

type Props = {
  storyPromotion: Story | null;
  message: MenuWebMessage | null;
  extraData?: {
    storyOwnerName: string;
  };
  useAsModal: boolean;
  onModalClosed?: () => void;
};

export default function InstallAppWarning({ storyPromotion, message, extraData, useAsModal, onModalClosed }: Props) {
  const [isVisible, setIsVisible] = useState(false);

  const { system, isMobileOS } = useGetSystemInformation();

  function closeModal() {
    setIsVisible(false);
    onModalClosed && onModalClosed();
  }

  useEffect(() => {
    if (!storyPromotion && !message) {
      setIsVisible(false);
      return;
    }
    setIsVisible(true && !testEnvPreventInstallAppModalWasSetted());
  }, [storyPromotion, message]);

  function handleOpenAndroidDownloadClick() {
    window.open(LINKS.ANDROID, '_blank');
  }

  function handleOpenIOSDownloadClick() {
    window.open(LINKS.IOS, '_blank');
  }

  function renderContent() {
    const description = storyPromotion ? parseHTMLString(getDescription()) : getDescription();
    return (
      <div className={classes.content}>
        {Boolean(storyPromotion || message) && (
          <div>
            <div className={classes.header}>
              <h4 className={classes.title}>{getTitle()}</h4>
              {useAsModal && (
                <IconButton onClick={closeModal} className={classes.closeButton} size="small">
                  <Close />
                </IconButton>
              )}
            </div>
            <p className={classes.description}>{description}</p>
          </div>
        )}
        <div className={classes.actionsBox}>
          {isMobileOS && system === Systems.Android && (
            <button
              onMouseDown={() =>
                ga.event({ action: 'Download', category: 'Download Android', label: 'banner_mobile', value: 0 })
              }
              className={classes.button}
              onClick={handleOpenAndroidDownloadClick}
              style={{ flex: 1 }}
            >
              <img src="/static/android.svg" />
              <span>Instalar no Android</span>
            </button>
          )}
          {isMobileOS && system === Systems.IOS && (
            <button
              onMouseDown={() =>
                ga.event({ action: 'Download', category: 'Download iPhone', label: 'banner_mobile', value: 0 })
              }
              className={classes.button}
              onClick={handleOpenIOSDownloadClick}
              style={{ flex: 1 }}
            >
              <img src="/static/apple.svg" />
              <span>Instalar no iPhone</span>
            </button>
          )}
          {!isMobileOS && (
            <>
              <button
                onMouseDown={() =>
                  ga.event({ action: 'Download', category: 'Download Android', label: 'banner_desktop', value: 0 })
                }
                className={classes.button}
                style={{ marginRight: 17.5 }}
                onClick={handleOpenAndroidDownloadClick}
              >
                <img src="/static/android.svg" />
                <span>Android</span>
              </button>
              <button
                onMouseDown={() =>
                  ga.event({ action: 'Download', category: 'Download iPhone', label: 'banner_desktop', value: 0 })
                }
                className={classes.button}
                onClick={handleOpenIOSDownloadClick}
              >
                <img src="/static/apple.svg" />
                <span>iPhone</span>
              </button>
            </>
          )}
        </div>
      </div>
    );

    function getTitle() {
      if (storyPromotion) {
        return 'Vai perder essa?';
      }
      return message?.title ?? '';
    }

    function getDescription() {
      if (storyPromotion) {
        const prizes = extractPromotionPrizes(storyPromotion.Legenda);
        const message = createPromotionMessage(prizes, extraData?.storyOwnerName ?? '');
        return message;
      }
      return message?.description ?? '';
    }
  }

  if (useAsModal) {
    return (
      <Modal
        open={isVisible}
        className={classes.modal}
        onClose={closeModal}
        BackdropProps={{
          invisible: true,
        }}
        disableScrollLock={true}
      >
        <Slide direction="up" in={isVisible} exit={!isVisible} timeout={200} mountOnEnter unmountOnExit>
          {renderContent()}
        </Slide>
      </Modal>
    );
  }

  return (
    <Fade in timeout={200}>
      {renderContent()}
    </Fade>
  );
}

function testEnvPreventInstallAppModalWasSetted() {
  return window?.Cypress?.env('prevent-install-app-modal') === 'true';
}

export function useFetchStoryWithPromotionOrDefaultMessage(cliqueiAcheiData: CliqueiAcheiData | null | undefined) {
  const { isLoggedIn } = useClientArea();
  const [message, setMesssage] = useState<MenuWebMessage | null>(null);
  const [storyPromotion, setStoryPromotion] = useState<Story | null>(null);

  const storiesWithPromotionIdMapToStoryPreview = useRef<{ [key: number]: StoryPreview }>({});

  useEffect(() => {
    async function loadStories() {
      // Need refactoring...

      if (ModalWarningStorage.modalAlreadyDisplayedInThisSession()) return;

      const city = getCityByUrl(cliqueiAcheiData?.Url_Cidade ?? '');
      if (!city) return;

      if (!isFranquisedCity(city.Url_Cidade)) return;

      const storiesWithPromotions: Story[] = [];
      const storiesPreview = await APICliqueiAchei.fetchStories(city.Url_Cidade);
      const storiesPreviewWithPromotion =
        storiesPreview.itens?.filter((storyPreview) => storyPreview.ContemPromocao) ?? [];
      for await (const storyPreview of storiesPreviewWithPromotion) {
        if (storyPreview.ProximoStorie?.Promocao) {
          storiesWithPromotions.push(storyPreview.ProximoStorie);
          storiesWithPromotionIdMapToStoryPreview.current[storyPreview.ProximoStorie.FotoId] = storyPreview;
        } else {
          const clientStories = await APICliqueiAchei.fetchClientStories(
            storyPreview.ClienteId,
            0,
            storyPreview.TotalStories
          );
          const storyWithPromotion = clientStories.itens?.find((story) => story.Promocao) ?? null;
          if (storyWithPromotion) {
            storiesWithPromotionIdMapToStoryPreview.current[storyWithPromotion.FotoId] = storyPreview;
            storiesWithPromotions.push(storyWithPromotion);
          }
        }
      }

      if (storiesWithPromotions.length > 0) {
        setStoryPromotion(getRandomArrayItem(storiesWithPromotions));
      } else {
        const response = await APIJsonConfigs.fetchMenuWebMessages();
        const randomMessage = getRandomArrayItem(response.messages);
        setMesssage(MenuWebMessageParser.parse(randomMessage, { estabelecimento: cliqueiAcheiData?.NomePuro ?? '' }));
      }
      ModalWarningStorage.saveModalAsAlreadyDisplayed();
    }

    loadStories();
  }, []);

  const getStoryPreviewFromStoryId = useCallback((photoId: number): StoryPreview | null => {
    return storiesWithPromotionIdMapToStoryPreview.current[photoId] ?? null;
  }, []);

  return {
    storyPromotion: isLoggedIn ? null : storyPromotion,
    defaultMessage: isLoggedIn ? null : message,
    getStoryPreviewFromStoryId,
  };
}

const ModalWarningStorage = {
  saveModalAsAlreadyDisplayed() {
    SessionStorage.setItem('install-app-warning-already-displayed', 'true');
  },
  modalAlreadyDisplayedInThisSession() {
    return SessionStorage.getItem('install-app-warning-already-displayed') === 'true';
  },
};
